import { useState, useEffect, useCallback } from 'react';
import { Video, ApiError } from '../types';

interface RecordsApiItem {
  videoId: string;
  title: string;
  channelId: string;
  channelTitle: string;
  thumbnailUrl: string;
  durationSeconds: number;
  viewCount: number;
  isShort: boolean;
  publishedAt: number | null;
  firstSeenAt: number;
  lastSeenAt: number;
}

interface RecordsApiResponse {
  categoryId: string;
  generatedAt: number;
  videos: RecordsApiItem[];
}

interface UseRecordsResult {
  videos: Video[];
  isLoading: boolean;
  error: string | null;
  fetchedAt: string | null;
  refetch: () => void;
}

function secondsToDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  let result = 'PT';
  if (hours > 0) result += `${hours}H`;
  if (minutes > 0) result += `${minutes}M`;
  if (secs > 0 || result === 'PT') result += `${secs}S`;
  return result;
}

function mapRecord(item: RecordsApiItem): Video {
  const published = item.publishedAt ?? item.firstSeenAt;
  return {
    id: item.videoId,
    title: item.title,
    channelTitle: item.channelTitle,
    channelId: item.channelId,
    thumbnail: item.thumbnailUrl,
    viewCount: item.viewCount,
    publishedAt: new Date(published * 1000).toISOString(),
    duration: secondsToDuration(item.durationSeconds),
    durationSeconds: item.durationSeconds,
    isShort: item.isShort,
  };
}

/**
 * Fetch the all-time "most viewed" records for a category. `enabled` lets the
 * app skip the request when the records view isn't active (hooks can't be
 * called conditionally, so we gate the fetch instead).
 */
export function useRecords(category: string, enabled = true): UseRecordsResult {
  const [videos, setVideos] = useState<Video[]>([]);
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [fetchedAt, setFetchedAt] = useState<string | null>(null);

  const fetchRecords = useCallback(async () => {
    if (!enabled) return;
    setIsLoading(true);
    setError(null);
    try {
      const categoryId = category === '0' ? 'ALL' : category;
      const params = new URLSearchParams({ category: categoryId, limit: '100' });
      const response = await fetch(`/api/records?${params.toString()}`);
      if (!response.ok) {
        const errorData = (await response.json()) as ApiError;
        throw new Error(errorData.error || 'Failed to fetch records');
      }
      const data = (await response.json()) as RecordsApiResponse;
      setVideos(data.videos.map(mapRecord));
      setFetchedAt(new Date(data.generatedAt).toISOString());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      setVideos([]);
    } finally {
      setIsLoading(false);
    }
  }, [category, enabled]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  return { videos, isLoading, error, fetchedAt, refetch: fetchRecords };
}
